import React, {
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useIntl } from 'umi';
import { EventEmitter } from 'events';
import { Bubble } from 'tea-component';
import { isEmpty } from 'lodash-es';
import classNames from 'classnames';
import { LANGUAGE_EN } from '@home/_config/config';
import { getLocalText } from '@/pages/datalab/utils/utils';
import type { IFilterValueRequest } from '@dbl/services/dashboard';
import {
  ILayoutItemFilter,
  IUserFilterScopeChart,
  TChangeTabLayoutsHandler,
} from '@dbl/core/dashboard';
import DateFilter from '@/pages/datalab/components/DashboardLayout/components/TopFilters/GeneralFilterGroup/DateFilter';
import StingFilter from '@/pages/datalab/components/DashboardLayout/components/TopFilters/GeneralFilterGroup/StingFilter';
import RegionFilter from '@/pages/datalab/components/DashboardLayout/components/TopFilters/GeneralFilterGroup/RegionFilter';
import NumberFilter from '@/pages/datalab/components/DashboardLayout/components/TopFilters/GeneralFilterGroup/NumberFilter';
import FilterEditWrap, {
  IFilterEditRenderProps,
  IFilterErrorInfo,
} from '@/pages/datalab/components/DashboardLayout/components/TopFilters/GeneralFilterGroup/GeneraFilterEditWrap';
import TipContext from '@home/components/TipContextComponents/TipContext';

type TFilterValueMap = Record<string, IFilterValueRequest>;
type TFilterComponentKey = 'date' | 'string' | 'country' | 'number';
type TFilterComponentType = React.ComponentType<Record<string, unknown>>;

interface IDependFilter {
  i?: string;
  filterName?: string;
}

interface IGeneralFilterInfo extends ILayoutItemFilter {
  filterType?: string;
  filterName?: string;
  operation?: string;
  value?: string[];
  scope?: {
    scope_charts?: IUserFilterScopeChart[];
  };
  dependFilters?: IDependFilter[];
  showScope?: boolean;
}

interface IFilterItemProps extends Partial<IFilterEditRenderProps> {
  filters: ILayoutItemFilter[];
  filterInfo: IGeneralFilterInfo;
  innerFilterValueMapRef: React.MutableRefObject<TFilterValueMap>;
  system?: 'datalab' | 'dashboard';
  eventEmitter: EventEmitter;
}

interface IGeneralFilterGroupProps {
  filters: ILayoutItemFilter[];
  isEdit: boolean;
  changeTabLayoutsReduce: TChangeTabLayoutsHandler;
  system: 'datalab' | 'dashboard';
  changeLeftLayoutSelectItem: (item: ILayoutItemFilter, isOpen?: boolean) => void;
}

export interface IGeneralFilterGroupRef {
  getFilters: () => IFilterValueRequest[];
  resetFilters: (filters: TFilterValueMap) => void;
  setFilterMapRef: (value: TFilterValueMap) => void;
  getFilterMapRef: () => TFilterValueMap;
}

export const errorClassNameMap: Record<number, string> = {
  10013: 'dbl-filter-drag-error',
  900000: 'dbl-filter-drag-error dbl-filter-drag-error-empty',
};

const filterComponent: Record<TFilterComponentKey, TFilterComponentType> = {
  'date': DateFilter,
  'string': StingFilter,
  'country': RegionFilter,
  'number': NumberFilter,
};

const getFilterComponent = (type?: string): TFilterComponentType => {
  if (type && Object.prototype.hasOwnProperty.call(filterComponent, type)) {
    return filterComponent[type as TFilterComponentKey];
  }
  // eslint-disable-next-line react/display-name
  return () => <></>;
};

const FilterItem: React.FC<IFilterItemProps> = (props) => {
  const {
    // filters,
    filterInfo,
    innerFilterValueMapRef,
    system = 'datalab',
    deleteVisible,
    eventEmitter,
    itemKey,
  } = props;
  const { showScope = true } = filterInfo || {};
  const { formatMessage, locale } = useIntl();
  const isEn = locale === LANGUAGE_EN;
  const divRef = useRef<HTMLDivElement>(null);
  // 定时器
  const timeoutRef = React.useRef<number | null>(null);
  // 记录鼠标是否划入气泡内部
  const isMouseEnterBubbleRef = useRef<boolean>(false);
  const [visibleScope, setVisibleScope] = useState(false);
  const [openStatus, setOpenStatus] = useState(false);
  const [errorInfoState, setErrorInfoState] = useState<IFilterErrorInfo>({});
  const [safeOffsetLeft, setSafeOffsetLeft] = useState<number>(0);
  
  // 解决报表预览态和编辑态，样式层级不一致的问题
  const errorInfo = useMemo(()=> {
    if (['date', 'number']?.includes(filterInfo?.filterType || '')) return {};
    if(props?.errorInfo) return props?.errorInfo;
    return errorInfoState;
  },[errorInfoState, props?.errorInfo, filterInfo?.filterType]);
  
  const setErrorInfo = (info: IFilterErrorInfo) => {
    if(props?.setErrorInfo) return props?.setErrorInfo?.(info);
    return setErrorInfoState(info);
  };
  
  const Component = getFilterComponent(filterInfo.filterType);
  
  // 使用 requestAnimationFrame 确保在下一帧执行
  useEffect(() => {
    let ele: Element | null | undefined = null;
    const onLeave = () => {
      // 当鼠标滑入气泡内部时, 不执行leave事件
      if (isMouseEnterBubbleRef.current) return;
      timeoutRef.current = window.setTimeout(() => {
        setVisibleScope(false);
      }, 300);
    };
    const onEnter = () => {
      timeoutRef.current && clearTimeout(timeoutRef.current);
      setVisibleScope(true);
    };
    
    const rafId = requestAnimationFrame(() => {
      // 这个ele 需要在requestAnimationFrame中获取, 才能保证每次都能添加事件成功, 否则有些情况下即便获取到了ele也无法添加成功
      ele = divRef.current?.querySelector(filterInfo.filterType === 'date' ? '.dbl-date-filter-name' : '.dbl-select-button-text');
      if (ele instanceof HTMLElement) {
        ele.addEventListener('mouseleave', onLeave);
        ele.addEventListener('mouseenter', onEnter);
      }
    });
    
    return () => {
      cancelAnimationFrame(rafId);
      // 清理定时器
      if (timeoutRef.current !== null) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      // 清理事件监听器
      if (ele instanceof HTMLElement) {
        ele.removeEventListener('mouseleave', onLeave);
        ele.removeEventListener('mouseenter', onEnter);
      }
    };
  }, [filterInfo, isEn]);
  
  // const baseOffsetLeft = useMemo(() => {
  //   // 历史逻辑：解决部分嵌入场景下，首个筛选器浮窗被遮挡/截取
  //   const firstFilter = filters?.[0] || {};
  //   const { i, filterType, filterName = '', operation } = filterInfo || {
  //     filterName: '',
  //   };
  //   if (props?.system === 'datalab' || firstFilter?.filterType === 'date') return 0;
  //   if (firstFilter?.i === i) {
  //     if (filterType === 'number') return (filterName?.length || 0) * 2;
  //     if (filterType === 'string') return (filterName + operation)?.length * 2;
  //   }
  //   return 0;
  // },[filterInfo, filters, props?.system]);

  const isInIframe = useMemo(() => {
    try {
      return window.self !== window.top;
    } catch (e) {
      // 某些跨域场景访问 window.top 会抛异常，这里视为 iframe 场景
      return true;
    }
  }, []);

  useLayoutEffect(() => {
    /**
     * 这里使用 useLayoutEffect（而非 useEffect）的原因：
     * - 本逻辑依赖 DOM 尺寸（getBoundingClientRect）计算浮层偏移；
     * - 需要在浏览器 paint 之前尽早修正位置，避免 hover 时浮层先渲染到错误位置再“跳动”到正确位置。
     *
     * 性能方面：
     * - 仅在 iframe 场景 + 浮层可见时触发；
     * - 核心测量放到 requestAnimationFrame 中执行，尽量降低阻塞 paint 的概率。
     */
    if (!isInIframe || !visibleScope || openStatus || deleteVisible) {
      // setSafeOffsetLeft(0);
      return;
    }

    const reference = (divRef.current?.querySelector(
      filterInfo.filterType === 'date'
        ? '.dbl-date-filter-name'
        : '.dbl-select-button-text',
    ) || null) as HTMLElement | null;
    if (!reference) {
      setSafeOffsetLeft(0);
      return;
    }

    // `DashboardLayout/index.scss` 中该浮层的 min-width 为 378px
    const bubbleMinWidth = 378;
    const viewportPadding = 8;

    const calc = () => {
      const rect = reference.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const bubbleEl = document.querySelector(
        '.dbl-dashboard-filter-info-bubble .tea-bubble',
      ) as HTMLElement | null;
      const bubbleWidth = Math.max(
        bubbleMinWidth,
        bubbleEl?.getBoundingClientRect().width ?? 0,
      );

      const baseLeft = centerX - bubbleWidth / 2;

      const minOffset = viewportPadding - baseLeft;
      const maxOffset =
        (window.innerWidth - viewportPadding - bubbleWidth) - baseLeft;

      // 只在需要时做边界修正：优先使用 0，否则取离 0 最近的边界值
      let nextOffset = 0;
      if (0 < minOffset) nextOffset = minOffset;
      if (0 > maxOffset) nextOffset = maxOffset;
      setSafeOffsetLeft(Number.isFinite(nextOffset) ? nextOffset : 0);
    };

    // 下一帧再测量，确保 Bubble DOM 已挂载且减少同步阻塞
    const rafId = window.requestAnimationFrame(calc);
    return () => {
      window.cancelAnimationFrame(rafId);
    };
  }, [
    deleteVisible,
    filterInfo.filterType,
    isInIframe,
    openStatus,
    visibleScope,
  ]);
  
  const errorRenderMap: Record<number, (info: IFilterErrorInfo) => React.ReactNode> = {
    10013: () => (
      <div className={'dbl-filter-drag-error-tip'}>
        {formatMessage({id: 'dbl_common_filter_invalid'})}
      </div>
    ),
    // itemKey 有值的时候说明是编辑态, 编辑态下显示空值提示, 预览态下不显示
    900000: () => itemKey ? (
      <div className={'dbl-filter-drag-error-tip dbl-filter-error-empty'}>
        <TipContext
          AWidth={495}
          tipText={formatMessage({ id: 'dbl_common_default_value_invalid_info' })}
          type={{
            hover: 'erry-tip-icon',
            default: 'erry-tip-icon',
          }}
        />
        {formatMessage({id: 'dbl_common_default_value_invalid'})}
      </div>
    ) : null,
  };

  const errorClassName = useMemo(() => {
    if (!errorInfo?.code) return '';
    if (errorInfo.code === 900000 && !itemKey) return ''; // 报表预览态下, 不显示空值提示
    return errorClassNameMap[errorInfo.code];
  }, [errorInfo?.code]);
  
  const renderErrorComponent = () => {
    if (!errorInfo?.code) return null;
    const render = errorRenderMap[errorInfo.code];
    if (render) {
      return render(errorInfo);
    }
    return null;
  };
  return isEmpty(innerFilterValueMapRef.current?.[filterInfo?.i]) ? (<div className={'dbl-filter-drag'}><div className="dbl-filter-drag-text">{formatMessage({id: 'dbl_create_dashboard_filter'})}</div></div>) : (
    <span>
      <div
        className={classNames(
          'dbl-filter-drag',
          filterInfo.filterType,
          errorClassName,
        )}
      >
        <Bubble
          content={(<>
 
            <div className={'item title'}>{formatMessage({id: 'dbl_create_dashboard_scope'})}</div>
            {
              filterInfo?.scope?.scope_charts?.map((chart: IUserFilterScopeChart) => (<div className={'item'} key={chart.task_id}>
                {getLocalText(isEn, chart?.nameCn, chart?.name)}
              </div>))
            }
            
            {
              (filterInfo?.dependFilters?.length ?? 0) > 0 ? (<div style={{marginTop: '5px'}}>
                <div className={'item title'}>{formatMessage({id: 'dbl_create_dashboard_depend'})}</div>
                {
                  filterInfo?.dependFilters?.map((filter: IDependFilter) => (<div className={'item'} key={filter?.i}>
                    {filter?.filterName ?? ''}
                  </div>))
                }
              </div>) : ''
            }
          
          </>)}
          visible={showScope && !!filterInfo.value && !openStatus && !deleteVisible && visibleScope}
          dark
          placement={"bottom"}
          placementOffset={[
            safeOffsetLeft,
            filterInfo.filterType === 'date' ? 15 : 9,
          ]}
          overlayClassName={'dbl-dashboard-filter-info-bubble'}
          flipped={false}
          escapeWithReference={true}
          // positionFixed = {false}
          referenceElement={divRef.current?.querySelector(filterInfo.filterType === 'date' ? '.dbl-date-filter-name' : '.dbl-select-button-text') || undefined}
          
          onMouseEnter={() => {
            isMouseEnterBubbleRef.current = true;
            // 鼠标滑入时 取消 之前定时关闭气泡的setTimeout
            timeoutRef.current && clearTimeout(timeoutRef.current);
            setVisibleScope(true);
          }}
          onMouseLeave={() => {
            isMouseEnterBubbleRef.current = false;
            timeoutRef.current = window.setTimeout(() => {
              setVisibleScope(false);
            }, 300);
          }}
        >
          <Component
            ref={divRef}
            innerFilterValueMapRef={innerFilterValueMapRef}
            system={system}
            filterInfo={filterInfo}
            errorInfo={errorInfo}
            setErrorInfo={setErrorInfo}
            setOpenStatus={setOpenStatus}
            setVisibleScope={setVisibleScope}
            eventEmitter={eventEmitter}
          />
        </Bubble>
      </div>
      {renderErrorComponent()}
    </span>
  );
};

const GeneralFilterGroup: React.ForwardRefRenderFunction<IGeneralFilterGroupRef, IGeneralFilterGroupProps> = (props, ref) => {
  const {filters, isEdit, changeTabLayoutsReduce, system, changeLeftLayoutSelectItem } = props;
  const eventEmitterRef = useRef<EventEmitter>(new EventEmitter());
  
  /**
   * 用于承上启下的 筛选器数据保存结构, 同时也符合 上传接口的 结构, 无需二次处理
   *  对上: 点击Confirm或Reset时, 传递给父组件 最新的所有筛选器的值, 用于触发图表重新渲染
   *  对下:
   *    * 保存所有筛选器的值, 同时确保在没有级联的情况下, 筛选器值变动后, 之间不互相影响
   *    * 有级联的情况下, 可以在各个筛选器组件内部 获取到其他筛选器的值
   */
  const innerFilterValueMapRef = useRef<TFilterValueMap>({});
  
  useImperativeHandle(ref, () => ({
    getFilters: () => Object.values(innerFilterValueMapRef.current),
    resetFilters: (filtersMap) => {
      innerFilterValueMapRef.current = filtersMap;
      // 触发reset事件, 通知子组件更新
      eventEmitterRef.current.emit('reset', filtersMap);
    },
    setFilterMapRef: (value) => { innerFilterValueMapRef.current = value },
    getFilterMapRef: () => innerFilterValueMapRef.current,
  }));

  return (<div className={'dbl-dashboard-filter-wrap-sortable'}>
    {
      isEdit ? (
        <FilterEditWrap
          filters={filters}
          changeTabLayoutsReduce={changeTabLayoutsReduce}
          changeLeftLayoutSelectItem={changeLeftLayoutSelectItem}
        >
          {
            (editProps: IFilterEditRenderProps) => (
              <FilterItem
                {...editProps}
                filters={filters}
                innerFilterValueMapRef={innerFilterValueMapRef}
                system={system}
                eventEmitter={eventEmitterRef.current}
              />)
          }
        
        </FilterEditWrap>
      ) : filters.map((item) => (
        <FilterItem
          key={item.i}
          filters={filters}
          filterInfo={item}
          innerFilterValueMapRef={innerFilterValueMapRef}
          system={system}
          eventEmitter={eventEmitterRef.current}
        />))
    }
  </div>);
  
};

export default React.forwardRef<IGeneralFilterGroupRef, IGeneralFilterGroupProps>(GeneralFilterGroup);